import { type MouseEvent, memo, useCallback, useMemo } from "react";

import { useProduct } from "@shopify/hydrogen-react";

import {
  TablescapeContext,
  TablescapeEvent,
} from "@root/tablescape/utilities/tablescapeContext";
import { STOREFRONT_CART_ATTRIBUTES } from "@tablescape/constants";
import {
  formatProductVariant,
  getMsrpForVariant,
  getPageSection,
} from "@tablescape/utilities";

import { ProductCardQuickAddView } from "./ProductCardQuickAdd.view";

type Props = {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  pageType?: string;
  quickAddButtonText?: string;
};

const ProductCardQuickAddBase = ({
  onMouseEnter,
  onMouseLeave,
  pageType,
  quickAddButtonText,
}: Props) => {
  const { selectedVariant } = useProduct();

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const onQuickAddClick = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!formattedSelectedVariant.availableForSale) {
        return;
      }
      TablescapeContext.emit(TablescapeEvent.TOGGLE_CART, {
        ui: {
          cartDrawerOpen: true,
          cartDrawerUserToggled: false,
          giftNoteDrawerOpen: false,
          productVariantDrawerOpen: false,
        },
      });

      const pageSection = getPageSection(e.currentTarget);

      const line = {
        id: formattedSelectedVariant.id,
        quantity: 1,
        attributes: [
          {
            key: STOREFRONT_CART_ATTRIBUTES.MSRP,
            value: String(getMsrpForVariant(formattedSelectedVariant)),
          },
          ...(formattedSelectedVariant.shippingNote
            ? [
                {
                  key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
                  value: "true",
                },
              ]
            : []),
        ],
      };

      TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
        cart: {
          metadata: {
            location: "quick_add",
            pageSection,
            pageType,
          },
          lines: [line],
        },
      });
    },
    [formattedSelectedVariant, pageType]
  );

  return (
    <ProductCardQuickAddView
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onQuickAddClick={onQuickAddClick}
      quickAddButtonText={quickAddButtonText}
    />
  );
};

export const ProductCardQuickAdd = memo(ProductCardQuickAddBase);
